import React from 'react';
import type { RiskListName, SuspiciousPlayer } from '@/types';

export interface RiskListInfo {
  name: RiskListName;
  title: string;
  color: 'error' | 'warning';
  description: React.ReactNode;
  /** The order rows come in, for the "first 2,000" note. */
  order: string;
}

export const RISK_LISTS: RiskListInfo[] = [
  {
    name: 'suspiciousWinRates',
    title: 'Suspicious Win Rates',
    color: 'error',
    description: (
      <>
        Players with &gt;90% win rate and &gt;20 matches played. Counts the window&apos;s last 8
        days at most, so Last 30 days shows the same players as Last 7 days.
      </>
    ),
    order: 'best win rate first',
  },
  {
    name: 'economyOutliers',
    title: 'Economy Outliers',
    color: 'error',
    description: (
      <>
        Players with coin or premium balances more than 3 standard deviations above the average.
        Balances as they are now: the date range does not change this list.
      </>
    ),
    order: 'most coins first',
  },
  {
    name: 'rapidProgression',
    title: 'Rapid Progression',
    color: 'warning',
    description: (
      <>
        Accounts created in the window that are already above level 12, the p99 for accounts up to 7
        days old (threshold raised from 5 after launch).
      </>
    ),
    order: 'highest level first',
  },
  {
    name: 'largeTransactions',
    title: 'Large Transactions',
    color: 'warning',
    description: (
      <>
        Single currency transactions in the window, largest amount first (credits and debits alike).
        A ranking with no threshold, so there is no count: load as many as you need, up to 2,000.
      </>
    ),
    order: 'largest first',
  },
  {
    name: 'unpurchasedPremium',
    title: 'Unpurchased Premium',
    color: 'error',
    description: (
      <>
        Players holding premium currency with no positive premiumCurrency transaction from a
        legitimate source (purchase / admin_grant / promotional / FTUE / daily login). Classic
        memory-edit signal. Balances as they are now: the date range does not change this list.
      </>
    ),
    order: 'most premium first',
  },
  {
    name: 'divisionMatchMismatch',
    title: 'Division/Match Mismatch',
    color: 'warning',
    description: (
      <>
        Players with FanRating &gt;2000 but fewer than 50 matches played. High-division progression
        normally requires many matches; users who got there without playing are a boosting / cheat
        signal. As they are now: the date range does not change this list.
      </>
    ),
    order: 'highest FanRating first',
  },
];

export const riskRowKey = (r: SuspiciousPlayer) => r.id ?? r.userId;
